// Drawing App - Custom Brush Editor
// 128x128 mini canvas for drawing custom brush tip shapes

import { CustomBrushData } from './types';

const EDITOR_SIZE = 128;
const DISPLAY_SCALE = 2; // Render at 2x for retina-like display

export class CustomBrushEditor {
  private modal: HTMLDivElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private isDrawing = false;
  private brushSize = 8;
  private isErasing = false;
  private onApply: ((data: CustomBrushData) => void) | null = null;
  private onClear: (() => void) | null = null;
  private lastPos: { x: number; y: number } | null = null;

  open(onApply: (data: CustomBrushData) => void, onClear: () => void, existingDataURL?: string | null): void {
    this.onApply = onApply;
    this.onClear = onClear;
    this.createModal();

    if (existingDataURL) {
      const img = new Image();
      img.onload = () => {
        if (this.ctx) {
          this.ctx.drawImage(img, 0, 0, EDITOR_SIZE, EDITOR_SIZE);
        }
      };
      img.src = existingDataURL;
    }
  }

  close(): void {
    if (this.modal) {
      this.modal.remove();
      this.modal = null;
      this.canvas = null;
      this.ctx = null;
    }
  }

  private createModal(): void {
    this.close();

    this.modal = document.createElement('div');
    this.modal.className = 'fixed inset-0 z-[100] flex items-center justify-center bg-black/60';
    this.modal.innerHTML = `
      <div class="bg-gray-800 rounded-xl shadow-2xl p-4 max-w-xs w-full mx-4" data-custom-brush-panel>
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-white text-sm font-medium">커스텀 브러시 편집</h3>
          <button class="cb-close text-gray-400 hover:text-white">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
        <div class="text-xs text-gray-400 mb-2">흰색으로 그리면 불투명, 검은 배경은 투명</div>
        <div class="flex justify-center mb-3">
          <canvas class="cb-canvas border border-gray-600 rounded cursor-crosshair" width="${EDITOR_SIZE}" height="${EDITOR_SIZE}" style="width:${EDITOR_SIZE * DISPLAY_SCALE}px;height:${EDITOR_SIZE * DISPLAY_SCALE}px;image-rendering:pixelated;"></canvas>
        </div>
        <div class="flex items-center space-x-2 mb-3">
          <span class="text-gray-400 text-xs">크기:</span>
          <button class="cb-size px-2 py-0.5 rounded text-xs bg-gray-700 text-gray-300 hover:bg-gray-600" data-size="3">3px</button>
          <button class="cb-size px-2 py-0.5 rounded text-xs bg-blue-600 text-white" data-size="8">8px</button>
          <button class="cb-size px-2 py-0.5 rounded text-xs bg-gray-700 text-gray-300 hover:bg-gray-600" data-size="20">20px</button>
          <div class="border-l border-gray-600 h-4 mx-1"></div>
          <button class="cb-mode-draw px-2 py-0.5 rounded text-xs bg-blue-600 text-white">그리기</button>
          <button class="cb-mode-erase px-2 py-0.5 rounded text-xs bg-gray-700 text-gray-300 hover:bg-gray-600">지우기</button>
        </div>
        <div class="flex items-center space-x-2">
          <button class="cb-clear px-3 py-1.5 rounded text-xs bg-gray-700 text-gray-300 hover:bg-gray-600">초기화</button>
          <button class="cb-default px-3 py-1.5 rounded text-xs bg-gray-700 text-gray-300 hover:bg-gray-600">기본 원형</button>
          <button class="cb-remove px-3 py-1.5 rounded text-xs bg-red-700/50 text-red-300 hover:bg-red-700">제거</button>
          <button class="cb-apply px-3 py-1.5 rounded text-xs bg-blue-600 text-white hover:bg-blue-500 ml-auto">적용</button>
        </div>
      </div>
    `;

    document.body.appendChild(this.modal);

    this.canvas = this.modal.querySelector('.cb-canvas') as HTMLCanvasElement;
    this.ctx = this.canvas.getContext('2d')!;

    // Fill with black (transparent)
    this.ctx.fillStyle = '#000000';
    this.ctx.fillRect(0, 0, EDITOR_SIZE, EDITOR_SIZE);

    this.bindEvents();
  }

  private bindEvents(): void {
    if (!this.modal || !this.canvas) return;

    // Close modal
    this.modal.querySelector('.cb-close')?.addEventListener('click', () => this.close());
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) this.close();
    });

    // Brush size buttons
    this.modal.querySelectorAll('.cb-size').forEach(btn => {
      btn.addEventListener('click', () => {
        this.brushSize = parseInt(btn.getAttribute('data-size') || '8');
        this.modal!.querySelectorAll('.cb-size').forEach(b => {
          b.classList.remove('bg-blue-600', 'text-white');
          b.classList.add('bg-gray-700', 'text-gray-300');
        });
        btn.classList.remove('bg-gray-700', 'text-gray-300');
        btn.classList.add('bg-blue-600', 'text-white');
      });
    });

    // Draw/Erase mode
    const drawBtn = this.modal.querySelector('.cb-mode-draw') as HTMLElement;
    const eraseBtn = this.modal.querySelector('.cb-mode-erase') as HTMLElement;
    drawBtn?.addEventListener('click', () => {
      this.isErasing = false;
      drawBtn.classList.remove('bg-gray-700', 'text-gray-300');
      drawBtn.classList.add('bg-blue-600', 'text-white');
      eraseBtn.classList.remove('bg-blue-600', 'text-white');
      eraseBtn.classList.add('bg-gray-700', 'text-gray-300');
    });
    eraseBtn?.addEventListener('click', () => {
      this.isErasing = true;
      eraseBtn.classList.remove('bg-gray-700', 'text-gray-300');
      eraseBtn.classList.add('bg-blue-600', 'text-white');
      drawBtn.classList.remove('bg-blue-600', 'text-white');
      drawBtn.classList.add('bg-gray-700', 'text-gray-300');
    });

    // Clear
    this.modal.querySelector('.cb-clear')?.addEventListener('click', () => {
      if (this.ctx) {
        this.ctx.fillStyle = '#000000';
        this.ctx.fillRect(0, 0, EDITOR_SIZE, EDITOR_SIZE);
      }
    });

    // Default circle
    this.modal.querySelector('.cb-default')?.addEventListener('click', () => {
      if (this.ctx) {
        this.ctx.fillStyle = '#000000';
        this.ctx.fillRect(0, 0, EDITOR_SIZE, EDITOR_SIZE);
        this.ctx.fillStyle = '#ffffff';
        this.ctx.beginPath();
        this.ctx.arc(EDITOR_SIZE / 2, EDITOR_SIZE / 2, EDITOR_SIZE / 2 - 4, 0, Math.PI * 2);
        this.ctx.fill();
      }
    });

    // Remove custom brush
    this.modal.querySelector('.cb-remove')?.addEventListener('click', () => {
      if (this.onClear) this.onClear();
      this.close();
    });

    // Apply
    this.modal.querySelector('.cb-apply')?.addEventListener('click', () => {
      if (this.canvas && this.onApply) {
        const dataURL = this.canvas.toDataURL('image/png');
        this.onApply({
          width: EDITOR_SIZE,
          height: EDITOR_SIZE,
          dataURL
        });
      }
      this.close();
    });

    // Canvas drawing events
    const getCanvasPos = (e: MouseEvent | Touch): { x: number; y: number } => {
      const rect = this.canvas!.getBoundingClientRect();
      return {
        x: (e.clientX - rect.left) / DISPLAY_SCALE,
        y: (e.clientY - rect.top) / DISPLAY_SCALE,
      };
    };

    const startDraw = (pos: { x: number; y: number }) => {
      this.isDrawing = true;
      this.lastPos = pos;
      this.drawDot(pos.x, pos.y);
    };

    const moveDraw = (pos: { x: number; y: number }) => {
      if (!this.isDrawing || !this.lastPos) return;
      this.drawLine(this.lastPos.x, this.lastPos.y, pos.x, pos.y);
      this.lastPos = pos;
    };

    const endDraw = () => {
      this.isDrawing = false;
      this.lastPos = null;
    };

    // Mouse events
    this.canvas.addEventListener('mousedown', (e) => {
      e.preventDefault();
      startDraw(getCanvasPos(e));
    });
    this.canvas.addEventListener('mousemove', (e) => {
      moveDraw(getCanvasPos(e));
    });
    this.canvas.addEventListener('mouseup', endDraw);
    this.canvas.addEventListener('mouseleave', endDraw);

    // Touch events
    this.canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      if (e.touches.length === 1) {
        startDraw(getCanvasPos(e.touches[0]));
      }
    }, { passive: false });
    this.canvas.addEventListener('touchmove', (e) => {
      e.preventDefault();
      if (e.touches.length === 1) {
        moveDraw(getCanvasPos(e.touches[0]));
      }
    }, { passive: false });
    this.canvas.addEventListener('touchend', endDraw);
  }

  private drawDot(x: number, y: number): void {
    if (!this.ctx) return;
    this.ctx.fillStyle = this.isErasing ? '#000000' : '#ffffff';
    this.ctx.beginPath();
    this.ctx.arc(x, y, this.brushSize / 2, 0, Math.PI * 2);
    this.ctx.fill();
  }

  private drawLine(x1: number, y1: number, x2: number, y2: number): void {
    if (!this.ctx) return;
    const dist = Math.hypot(x2 - x1, y2 - y1);
    const steps = Math.max(1, Math.ceil(dist / 2));

    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const x = x1 + (x2 - x1) * t;
      const y = y1 + (y2 - y1) * t;
      this.drawDot(x, y);
    }
  }
}
